import React, { useMemo, useRef, useState } from 'react'

import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import { Visibility } from '@material-ui/icons'
import { KeyboardDateTimePicker } from '@material-ui/pickers'
import { toast } from 'react-toastify'

import { Container } from '../../components'
import { LoriIcon } from '../../navigation/layouts/Lori/Icons/LoriIcon'
import { Selector } from '../../components/Selector/Selector'
import {
    ConcessionMessage,
    ConcessionMessagePreview,
    ConcessionMessageType,
    CreateConcessionMessageInput,
} from '../../models/Concession'
import {
    fetchMessagePreview,
    generateAiDraft,
    useConcessionMessages,
} from '../../hooks/useConcessionMessages'
import { RichTextEmailEditor } from '../Outbound/Campaign/CreateCampaign/RichTextEmailEditor'
import { ConcessionMergeVariableDropdown } from './ConcessionMergeVariableDropdown'
import { MESSAGE_STATUS_OPTIONS, MESSAGE_TYPE_OPTIONS } from './utils'
import { validateConcessionMessage } from './validation'
import { AI_TONE_PRESETS, useConcessionStyles } from './styles'

interface Props {
    concessionId: number
    lifetimeStart: string
    lifetimeEnd: string
    message?: ConcessionMessage
    onClose: () => void
}

/** Wrap plain text (from AI) into simple paragraph HTML for the rich editor. */
const plainToHtml = (text: string): string =>
    text
        .split(/\n{2,}/)
        .map((block) => `<p>${block.replace(/\n/g, '<br/>')}</p>`)
        .join('')

export const ConcessionMessageForm: React.FC<Props> = ({
    concessionId,
    lifetimeStart,
    lifetimeEnd,
    message,
    onClose,
}) => {
    const theme = useTheme()
    const classes = useConcessionStyles()
    const { createMessage, updateMessage } = useConcessionMessages(concessionId)

    const [messageType, setMessageType] = useState<ConcessionMessageType>(
        message?.message_type ?? 'email',
    )
    const [subject, setSubject] = useState(message?.subject ?? '')
    const [content, setContent] = useState(message?.content ?? '')
    const [contentHtml, setContentHtml] = useState(message?.content_html ?? '')
    const [sendAt, setSendAt] = useState<Date | null>(
        message?.send_at ? new Date(message.send_at) : null,
    )
    const [status, setStatus] = useState<
        CreateConcessionMessageInput['status']
    >(
        (message?.status as CreateConcessionMessageInput['status']) ??
            'scheduled',
    )
    const [errors, setErrors] = useState<string[]>([])

    // Remount key so the rich editor re-reads htmlValue after AI fills it.
    const [editorKey, setEditorKey] = useState(0)
    // Insert-at-cursor handle exposed by the rich editor.
    const insertRef = useRef<((text: string) => void) | null>(null)

    // AI draft dialog.
    const [aiOpen, setAiOpen] = useState(false)
    const [aiTone, setAiTone] = useState('')
    const [aiPrompt, setAiPrompt] = useState('')
    const [aiLoading, setAiLoading] = useState(false)

    // Preview dialog.
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [preview, setPreview] = useState<ConcessionMessagePreview | null>(
        null,
    )

    const selectedTypeId = useMemo(
        () =>
            MESSAGE_TYPE_OPTIONS.find((o) => o.value === messageType)?.id ?? 0,
        [messageType],
    )
    const selectedStatusId = useMemo(
        () => MESSAGE_STATUS_OPTIONS.find((o) => o.value === status)?.id ?? 0,
        [status],
    )

    const isSaving = createMessage.isLoading || updateMessage.isLoading
    const isEmail = messageType === 'email'

    const handleSave = () => {
        const validationErrors = validateConcessionMessage({
            message_type: messageType,
            subject: isEmail ? subject : '',
            content,
            content_html: isEmail ? contentHtml : '',
            send_at: sendAt,
            lifetime_start: lifetimeStart,
            lifetime_end: lifetimeEnd,
        })
        if (validationErrors.length > 0) {
            setErrors(validationErrors)
            return
        }
        setErrors([])

        const payload: CreateConcessionMessageInput = {
            concession: concessionId,
            message_type: messageType,
            content: content.trim(),
            send_at: sendAt ? sendAt.toISOString() : '',
            status,
        }
        if (isEmail) {
            payload.subject = subject.trim()
            payload.content_html = contentHtml
        }

        if (message) {
            const { concession: _concession, ...body } = payload
            updateMessage.mutate(
                { id: message.id, body },
                { onSuccess: () => onClose() },
            )
        } else {
            createMessage.mutate(payload, { onSuccess: () => onClose() })
        }
    }

    const insertIntoBody = (tag: string) => {
        if (insertRef.current) {
            insertRef.current(tag)
        } else {
            setContent((c) => c + tag)
        }
    }

    const handleGenerateAi = async () => {
        setAiLoading(true)
        try {
            const res = await generateAiDraft({
                concession: concessionId,
                message_type: messageType,
                tone: aiTone.trim() || undefined,
                prompt: aiPrompt.trim() || undefined,
            })
            if (isEmail) {
                if (res.subject !== undefined) setSubject(res.subject)
                setContent(res.content)
                setContentHtml(plainToHtml(res.content))
                setEditorKey((k) => k + 1)
            } else {
                setContent(res.content)
            }
            setAiOpen(false)
            toast.success('Lori drafted your message')
        } catch {
            toast.error('Lori could not generate a draft')
        } finally {
            setAiLoading(false)
        }
    }

    const handlePreview = async () => {
        if (!message) return
        setPreviewOpen(true)
        setPreviewLoading(true)
        try {
            const res = await fetchMessagePreview(message.id)
            setPreview(res)
        } catch {
            setPreview(null)
            toast.error('Failed to load preview')
        } finally {
            setPreviewLoading(false)
        }
    }

    const fieldSpacing = { marginBottom: theme.spacing(2), width: '100%' }

    return (
        <Container direction="column" style={{ width: '100%' }}>
            <Selector
                label="Message type"
                size="small"
                currentValue={selectedTypeId}
                data={MESSAGE_TYPE_OPTIONS}
                getDisplayString={(o) => o.label}
                onChange={(e) => {
                    const id = e.target.value as number
                    const option = MESSAGE_TYPE_OPTIONS.find((o) => o.id === id)
                    if (option) setMessageType(option.value)
                }}
                customStyle={{ formControl: fieldSpacing }}
            />

            {/* Editor tools: AI draft + preview */}
            <Container
                style={{
                    gap: theme.spacing(1),
                    marginBottom: theme.spacing(2),
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                        <LoriIcon width={18} height={18} color="#008C85" />
                    }
                    onClick={() => setAiOpen(true)}
                    style={{ textTransform: 'none' }}
                >
                    Draft with Lori
                </Button>
                {message && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={handlePreview}
                        style={{ textTransform: 'none' }}
                    >
                        Preview
                    </Button>
                )}
            </Container>

            {isEmail && (
                <TextField
                    label="Subject"
                    variant="outlined"
                    size="small"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <ConcessionMergeVariableDropdown
                                    onSelect={(tag) =>
                                        setSubject((s) => s + tag)
                                    }
                                />
                            </InputAdornment>
                        ),
                    }}
                    style={fieldSpacing}
                />
            )}

            {isEmail ? (
                <Container direction="column" style={fieldSpacing}>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ marginBottom: theme.spacing(0.5) }}
                    >
                        Email body
                    </Typography>
                    <RichTextEmailEditor
                        key={editorKey}
                        value={content}
                        htmlValue={contentHtml}
                        placeholder="Write the email tenants will receive..."
                        onChange={(plainText, html) => {
                            setContent(plainText)
                            setContentHtml(html)
                        }}
                        onInsertRef={(fn) => {
                            insertRef.current = fn
                        }}
                        toolbarExtra={
                            <ConcessionMergeVariableDropdown
                                onSelect={insertIntoBody}
                            />
                        }
                    />
                </Container>
            ) : (
                <Container direction="column" style={fieldSpacing}>
                    <Container
                        alignItems="center"
                        style={{ marginBottom: theme.spacing(0.5) }}
                    >
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ flex: 1 }}
                        >
                            Message
                        </Typography>
                        <ConcessionMergeVariableDropdown
                            onSelect={(tag) => setContent((c) => c + tag)}
                        />
                    </Container>
                    <TextField
                        variant="outlined"
                        size="small"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        multiline
                        rows={3}
                        placeholder="Write the text message tenants will receive..."
                        fullWidth
                    />
                </Container>
            )}

            <Container style={{ gap: theme.spacing(2), ...fieldSpacing }}>
                <KeyboardDateTimePicker
                    label="Send at"
                    inputVariant="outlined"
                    size="small"
                    format="MM/dd/yyyy hh:mm a"
                    value={sendAt}
                    onChange={(date) => setSendAt(date)}
                    style={{ flex: 1 }}
                />
                <Selector
                    label="Status"
                    size="small"
                    currentValue={selectedStatusId}
                    data={MESSAGE_STATUS_OPTIONS}
                    getDisplayString={(o) => o.label}
                    onChange={(e) => {
                        const id = e.target.value as number
                        const option = MESSAGE_STATUS_OPTIONS.find(
                            (o) => o.id === id,
                        )
                        if (option) setStatus(option.value)
                    }}
                    customStyle={{ formControl: { flex: 1, width: '100%' } }}
                />
            </Container>

            {errors.length > 0 && (
                <Container
                    direction="column"
                    style={{
                        marginBottom: theme.spacing(2),
                        padding: theme.spacing(1.5),
                        backgroundColor: theme.palette.error.light,
                        borderRadius: 4,
                    }}
                >
                    {errors.map((err, i) => (
                        <Typography
                            key={i}
                            variant="body2"
                            style={{ color: theme.palette.error.dark }}
                        >
                            {err}
                        </Typography>
                    ))}
                </Container>
            )}

            <Container
                justifyContent="flex-end"
                style={{ gap: theme.spacing(1.5) }}
            >
                <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={onClose}
                    disabled={isSaving}
                    className={classes.cancelButton}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={isSaving}
                    className={classes.saveButton}
                >
                    {message ? 'Save Message' : 'Schedule Message'}
                </Button>
            </Container>

            {/* AI draft dialog */}
            <Dialog
                open={aiOpen}
                onClose={() => (aiLoading ? undefined : setAiOpen(false))}
                maxWidth="sm"
                fullWidth
                classes={{ paper: classes.aiDialogPaper }}
            >
                <div className={classes.aiHeader}>
                    <div className={classes.aiHeaderIcon}>
                        <LoriIcon width={26} height={26} color="#ffffff" />
                    </div>
                    <Container direction="column" flex={1}>
                        <span className={classes.aiHeaderTitle}>
                            Draft with Lori
                        </span>
                        <span className={classes.aiHeaderSubtitle}>
                            Lori will draft a starting{' '}
                            {isEmail ? 'subject and body' : 'message'} — then
                            personalize with merge variables.
                        </span>
                    </Container>
                </div>

                <DialogContent style={{ paddingTop: theme.spacing(3) }}>
                    <Typography className={classes.aiFieldLabel}>
                        Tone
                    </Typography>
                    <div className={classes.toneChips}>
                        {AI_TONE_PRESETS.map((preset) => {
                            const active = aiTone === preset
                            return (
                                <Chip
                                    key={preset}
                                    label={preset}
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                        setAiTone(active ? '' : preset)
                                    }
                                    className={
                                        active
                                            ? classes.toneChipActive
                                            : classes.toneChip
                                    }
                                />
                            )
                        })}
                    </div>
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Or describe a custom tone…"
                        value={aiTone}
                        onChange={(e) => setAiTone(e.target.value)}
                        style={{ marginBottom: theme.spacing(3) }}
                    />

                    <Typography className={classes.aiFieldLabel}>
                        What should it emphasize?{' '}
                        <span
                            style={{
                                fontWeight: 400,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            (optional)
                        </span>
                    </Typography>
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="e.g. Emphasize locking in savings before the offer drops"
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                    />
                </DialogContent>
                <DialogActions style={{ padding: theme.spacing(2, 3) }}>
                    <Button
                        onClick={() => setAiOpen(false)}
                        disabled={aiLoading}
                        className={classes.cancelButton}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleGenerateAi}
                        disabled={aiLoading}
                        className={classes.saveButton}
                        variant="contained"
                        startIcon={
                            aiLoading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <LoriIcon
                                    width={18}
                                    height={18}
                                    color="#ffffff"
                                />
                            )
                        }
                    >
                        {aiLoading ? 'Lori is writing…' : 'Generate with Lori'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview dialog */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Preview
                    {preview?.tenant_name ? ` — ${preview.tenant_name}` : ''}
                </DialogTitle>
                <DialogContent>
                    {previewLoading ? (
                        <Container
                            justifyContent="center"
                            style={{ padding: 24 }}
                        >
                            <CircularProgress size={24} />
                        </Container>
                    ) : !preview ? (
                        <Typography variant="body2" color="textSecondary">
                            No preview available.
                        </Typography>
                    ) : (
                        <Container direction="column">
                            {isEmail && preview.subject && (
                                <Typography
                                    variant="subtitle2"
                                    style={{
                                        fontWeight: 700,
                                        marginBottom: theme.spacing(1),
                                    }}
                                >
                                    {preview.subject}
                                </Typography>
                            )}
                            {isEmail && preview.content_html ? (
                                <div
                                    // Resolved server-side; safe to render.
                                    dangerouslySetInnerHTML={{
                                        __html: preview.content_html,
                                    }}
                                />
                            ) : (
                                <Typography
                                    variant="body2"
                                    style={{ whiteSpace: 'pre-wrap' }}
                                >
                                    {preview.content}
                                </Typography>
                            )}
                        </Container>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setPreviewOpen(false)}
                        style={{ textTransform: 'none' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
